import { SEO_PAGES, DEFAULT_SEO, PageSEOMeta } from './seo-config';
import { SITE_URL } from './headMeta';
import { HOW_TO_ROOT, howToCategorySeo, howToTopicSeo } from './howToCategories';

export { SITE_URL };

/** Canonical Organization entity reused across all schema blocks. */
export const ORG = {
  '@type': 'Organization',
  name: 'Cendien',
  legalName: 'Arisma Group, LLC',
  url: SITE_URL,
  foundingDate: '1999-09-17',
  numberOfEmployees: { '@type': 'QuantitativeValue', minValue: 20, maxValue: 50 },
  telephone: '+1-214-245-4580',
  email: 'info@cendien.com',
  address: {
    '@type': 'PostalAddress',
    streetAddress: '1846 E. Rosemeade Pkwy, Suite 200',
    addressLocality: 'Carrollton',
    addressRegion: 'TX',
    postalCode: '75007',
    addressCountry: 'US',
  },
  areaServed: ['Dallas–Fort Worth', 'Texas', 'United States'],
  naics: ['541512', '541519'],
  knowsAbout: [
    'Infor',
    'Infor Lawson',
    'Infor CloudSuite',
    'Workday support',
    'Oracle',
    'PeopleSoft',
    'Managed IT services',
    'Co-managed IT',
    'Cybersecurity',
    'IT staffing',
    'AI agents',
    'Microsoft Copilot',
  ],
  hasCredential: [
    {
      '@type': 'EducationalOccupationalCredential',
      name: 'Texas DIR Vendor',
      credentialCategory: 'registration',
    },
    {
      '@type': 'EducationalOccupationalCredential',
      name: 'Certified MBE — NCTRCA #HMMB47549N0725',
      credentialCategory: 'certification',
      recognizedBy: {
        '@type': 'Organization',
        name: 'North Central Texas Regional Certification Agency (NCTRCA)',
      },
    },
    {
      '@type': 'EducationalOccupationalCredential',
      name: 'Microsoft Certified Partner',
      credentialCategory: 'certification',
      recognizedBy: { '@type': 'Organization', name: 'Microsoft' },
    },
  ],
  award: [
    'NCTRCA Certified Minority Business Enterprise (MBE)',
    'Texas DIR Vendor',
  ],
  // The profiles actually linked on the site (footer/social + public directories).
  sameAs: [
    'https://www.linkedin.com/company/cendien',
    'https://www.g2.com/sellers/cendien',
    'https://builtin.com/company/cendien',
    'https://www.designrush.com/agency/profile/cendien',
    'https://erpresearch.com/infor-partners/arisma-group-llc-dba-cendien',
    'https://www.instagram.com/cendien',
    'https://www.facebook.com/cendien',
    'https://www.youtube.com/@cendien',
  ],
};

const BREADCRUMB_LABELS: Record<string, string> = {
  about: 'About',
  'our-story': 'Our Story',
  'mission-values': 'Mission & Values',
  'why-cendien': 'Why Cendien',
  certifications: 'Certifications',
  'dallas-coverage': 'Dallas Coverage',
  contact: 'Contact',
  services: 'Services',
  staffing: 'Staffing',
  contract: 'Contract Staffing',
  'direct-hire': 'Direct Hire',
  'managed-teams': 'Managed Teams',
  'executive-search': 'Executive Search',
  erp: 'ERP',
  implementation: 'Implementation',
  'managed-services': 'Managed Services',
  'infor-support': 'Infor Lawson & CloudSuite Support',
  'infor-cloudsuite': 'Infor CloudSuite Managed Services',
  workday: 'Workday',
  'infor-lawson': 'Infor Lawson',
  'oracle-peoplesoft': 'Oracle PeopleSoft',
  'microsoft-dynamics': 'Microsoft Dynamics',
  'erp-general': 'ERP (General)',
  'workday-support': 'Workday Support & AMS',
  upgrades: 'Upgrades',
  integration: 'Integration',
  training: 'Training',
  consulting: 'Consulting',
  managed: 'Managed IT',
  infrastructure: 'Infrastructure',
  security: 'Security',
  'service-desk': 'Service Desk',
  'l1-l2-support': 'L1/L2 Support',
  'field-support': 'Field Support',
  ai: 'AI & Automation',
  strategy: 'AI Strategy',
  automation: 'Automation',
  'machine-learning': 'Machine Learning',
  'generative-ai': 'Generative AI',
  'app-development': 'App Development',
  'business-intelligence': 'Business Intelligence',
  'ai-data': 'AI & Data',
  'healthcare-it': 'Healthcare IT',
  ehr: 'EHR',
  interoperability: 'Interoperability',
  cloud: 'Cloud',
  migration: 'Cloud Migration',
  'managed-cloud': 'Managed Cloud',
  app: 'Applications',
  'development-services': 'Development Services',
  'cloud-services': 'Cloud Services',
  'virtual-cio': 'Virtual CIO',
  'strategy-advisory': 'Strategy Advisory',
  'digital-transformation': 'Digital Transformation',
  'tech-assessment': 'Tech Assessment',
  'managed-compliance': 'Managed Compliance',
  'change-management': 'Change Management',
  industries: 'Industries',
  'government-contracting': 'Government Contracting',
  'engagement-models': 'Engagement Models',
  healthcare: 'Healthcare',
  government: 'Government',
  commercial: 'Commercial',
  transportation: 'Transportation',
  education: 'Education',
  energy: 'Energy',
  manufacturing: 'Manufacturing',
  'financial-services': 'Financial Services',
  retail: 'Retail',
  nonprofit: 'Nonprofit',
  legal: 'Legal',
  hospitality: 'Hospitality',
  partnerships: 'Partnerships',
  infor: 'Infor',
  microsoft: 'Microsoft',
  aws: 'AWS',
  oracle: 'Oracle',
  connectwise: 'ConnectWise',
  careers: 'Careers',
  insights: 'Insights',
  videos: 'Videos',
  'how-tos': 'How-Tos',
  blog: 'Blog',
  resources: 'Resources',
  'case-studies': 'Case Studies',
  'request-consultants': 'Request Consultants',
  'privacy-policy': 'Privacy Policy',
  'terms-of-service': 'Terms of Service',
};

/** Build a BreadcrumbList schema from a pathname. */
export function buildBreadcrumbs(pathname: string): Record<string, unknown> {
  const segments = pathname.split('/').filter(Boolean);
  const items: Record<string, unknown>[] = [
    { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
  ];

  // Headerless landing pages (/managed-it/{slug}) sit under Managed IT Services;
  // /managed-it itself is only a redirect, so it never appears as a crumb.
  const LANDING_PARENTS: Record<string, [string, string]> = {
    'managed-it': ['Managed IT Services', '/services/managed'],
    'infor-support': ['Infor Support', '/services/erp/infor-support'],
    'workday-support': ['Workday Support', '/services/erp/workday-support'],
    'it-staffing': ['IT Staffing', '/services/staffing'],
    'ai-services': ['AI Services', '/services/ai'],
    'software-development': ['Development Services', '/services/app/development-services'],
  };
  if (LANDING_PARENTS[segments[0]] && segments[1]) {
    const meta = resolveSEO(pathname);
    const [parentName, parentPath] = LANDING_PARENTS[segments[0]];
    items.push(
      { '@type': 'ListItem', position: 2, name: parentName, item: `${SITE_URL}${parentPath}` },
      { '@type': 'ListItem', position: 3, name: meta.title.replace(/\s*\|\s*Cendien$/, ''), item: `${SITE_URL}${pathname}` },
    );
    return { '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: items };
  }

  let accPath = '';
  segments.forEach((seg, idx) => {
    accPath += `/${seg}`;
    items.push({
      '@type': 'ListItem',
      position: idx + 2,
      name:
        BREADCRUMB_LABELS[seg] ??
        seg.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
      item: `${SITE_URL}${accPath}`,
    });
  });

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items,
  };
}

/** Builds the full JSON-LD graph used by the runtime hook and the prerenderer. */
export function buildSchema(meta: PageSEOMeta, path: string): Record<string, unknown>[] {
  const pageUrl = `${SITE_URL}${path}`;
  const schemas: Record<string, unknown>[] = [];

  const webPageBase = {
    '@type':
      meta.schemaType === 'WebPage' || meta.schemaType === 'AboutPage' || meta.schemaType === 'ContactPage'
        ? meta.schemaType
        : 'WebPage',
    name: meta.title,
    description: meta.description,
    url: pageUrl,
    isPartOf: { '@type': 'WebSite', name: 'Cendien', url: SITE_URL },
    publisher: ORG,
    inLanguage: 'en-US',
  };

  // 1. Site-wide Organization — exactly one per page.
  schemas.push({ '@context': 'https://schema.org', ...ORG });

  // 2. Site-wide LocalBusiness (ProfessionalService) — exactly one per page,
  //    carrying the real NAP + service area used everywhere.
  schemas.push({
    '@context': 'https://schema.org',
    '@type': ['LocalBusiness', 'ProfessionalService'],
    '@id': `${SITE_URL}/#localbusiness`,
    name: 'Cendien',
    legalName: 'Arisma Group, LLC',
    url: SITE_URL,
    description: meta.description,
    foundingDate: '1999-09-17',
    numberOfEmployees: { '@type': 'QuantitativeValue', minValue: 20, maxValue: 50 },
    telephone: '+1-214-245-4580',
    email: 'info@cendien.com',
    address: ORG.address,
    geo: { '@type': 'GeoCoordinates', latitude: 32.9537, longitude: -96.8903 },
    areaServed: ['Dallas, TX', 'Fort Worth, TX', 'Carrollton, TX', 'Dallas–Fort Worth', 'Texas', 'United States'],
    sameAs: ORG.sameAs,
    knowsAbout: ORG.knowsAbout,
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '08:00',
        closes: '18:00',
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Saturday',
        description: 'By appointment',
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Sunday',
        description: 'Closed',
      },
    ],
  });

  // 3. WebSite entity with sitelinks search box.
  schemas.push({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Cendien',
    url: SITE_URL,
    description: meta.description,
    publisher: { '@type': 'Organization', name: 'Cendien', url: 'https://cendien.com' },
    potentialAction: {
      '@type': 'SearchAction',
      target: { '@type': 'EntryPoint', urlTemplate: `${SITE_URL}/search?q={search_term_string}` },
      'query-input': 'required name=search_term_string',
    },
  });

  // 4. Page entity.
  if (meta.schemaType === 'Blog') {
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'Blog',
      name: meta.title.split(' | ')[0],
      description: meta.description,
      url: pageUrl,
      publisher: ORG,
      inLanguage: 'en-US',
    });
  } else if (meta.schemaType === 'CollectionPage') {
    schemas.push({ '@context': 'https://schema.org', ...webPageBase, '@type': 'CollectionPage' });
  } else if (meta.schemaType === 'ContactPage') {
    schemas.push({ '@context': 'https://schema.org', ...webPageBase, '@type': 'ContactPage' });
  } else if (meta.schemaType === 'ProfessionalService') {
    // The service entity itself; the LocalBusiness block above carries the NAP.
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: meta.title.split(' | ')[0],
      serviceType: (meta.schemaExtras && (meta.schemaExtras.serviceType as string)) || meta.title.split(' | ')[0],
      description: meta.description,
      url: pageUrl,
      provider: { '@type': 'Organization', name: 'Cendien', url: SITE_URL },
      areaServed: (meta.schemaExtras?.areaServed as string | undefined) ?? ['Dallas, TX', 'Fort Worth, TX', 'Texas', 'United States'],
      ...(meta.schemaExtras?.image ? { image: meta.schemaExtras.image } : {}),
      ...(meta.schemaExtras?.offers ? { offers: meta.schemaExtras.offers } : {}),
    });
    schemas.push({ '@context': 'https://schema.org', ...webPageBase });
  } else {
    schemas.push({ '@context': 'https://schema.org', ...webPageBase });
  }

  // 5. Breadcrumbs — every page except home.
  if (path !== '/') schemas.push(buildBreadcrumbs(path));
  return schemas;
}

/** Resolves a pathname to its configured SEO meta (exact → how-to category → longest prefix → default). */
export function resolveSEO(pathname: string): PageSEOMeta {
  if (SEO_PAGES[pathname]) return SEO_PAGES[pathname];
  // How-to category pages (/videos/how-tos/{category} and .../erp/{sub}) derive
  // their meta from the taxonomy rather than the static page map.
  if (pathname.startsWith(`${HOW_TO_ROOT}/`)) {
    const rest = pathname.slice(HOW_TO_ROOT.length + 1).split('/').filter(Boolean);
    const catMeta = howToCategorySeo(rest[0] || '', rest[1]);
    if (catMeta) return catMeta;
    // Topic-chip pages (e.g. AI Agents) resolve from the same taxonomy engine.
    const topicMeta = howToTopicSeo(rest[0] || '');
    if (topicMeta) return topicMeta;
  }
  const prefix = Object.keys(SEO_PAGES)
    .filter((k) => pathname.startsWith(k) && k !== '/')
    .sort((a, b) => b.length - a.length)[0];
  return prefix ? SEO_PAGES[prefix] : DEFAULT_SEO;
}